<template>
  <div>
    <v-card class="mt-4" :loading="loading">
      <div class="grey lighten-3 pa-2">
        <v-text-field
          placeholder="Search"
          type="search"
          v-model="search"
          solo
          hide-details
          :disabled="loading"
        >
          <template v-slot:prepend-inner>
            <v-icon>$search</v-icon>
          </template>

          <template v-slot:append>
            <v-tooltip top v-if="search.length > 0">
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-on="on" v-bind="attrs" icon small @click="search = ''">
                  <v-icon>$close</v-icon>
                </v-btn>
              </template>
              <span>Clear</span>
            </v-tooltip>
          </template>
        </v-text-field>
        <v-slide-y-transition hide-on-leave>
          <div class="px-2 pt-2" v-if="pathEntities.length > 0">
            <a
              href="#"
              class="font-weight-bold"
              :class="{ disabled: loading }"
              @click.stop.prevent="path = null"
              >All</a
            >
            <template v-for="(entity, i) in pathEntities">
              <v-icon :key="i + '_divider'" class="mx-2">$right</v-icon>
              <a
                v-if="i !== pathEntities.length - 1"
                :key="i + '_crumb'"
                href="#"
                class="font-weight-bold"
                :class="{ disabled: loading }"
                >{{ entity.name }}</a
              >
              <span :key="i + '_crumb'" v-else>{{ entity.name }}</span>
            </template>
          </div>
        </v-slide-y-transition>
      </div>
      <v-list class="pa-0">
        <v-list-item v-if="pageItems.length == 0">
          <v-list-item-content class="justify-center text-caption">
            - No Results Found -
          </v-list-item-content>
        </v-list-item>
        <template v-for="item in pageItems">
          <v-divider :key="item.path + '_divider'"></v-divider>
          <v-list-item :key="item.path + '_li'">
            <v-list-item-content class="py-5">
              <v-list-item-title class="text-h6">{{
                item.name
              }}</v-list-item-title>
              <v-list-item-subtitle class="mt-2">
                <v-icon small style="font-size: 0.5rem" class="mr-1">
                  $mapMarker
                </v-icon>
                {{ item.displayAddress }}
              </v-list-item-subtitle>
              <v-list-item-subtitle class="mt-1">
                <v-icon small style="font-size: 90%" class="mr-1">
                  $phone
                </v-icon>
                {{ item.displayPhone }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="d-flex align-center flex-row">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    elevation="0"
                    fab
                    small
                    color="grey lighten-3"
                    v-on="on"
                    v-bind="attrs"
                    class="mx-1"
                    @click="$emit('edit', { entity: item, path: item.path })"
                    :disabled="loading"
                  >
                    <v-icon>$edit</v-icon>
                  </v-btn>
                </template>
                <span>Edit</span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    elevation="0"
                    fab
                    small
                    color="grey lighten-3"
                    v-on="on"
                    v-bind="attrs"
                    class="mx-1"
                    @click="
                      $emit('showContacts', { entity: item, path: item.path })
                    "
                    :disabled="loading"
                  >
                    <v-icon>$contacts</v-icon>
                  </v-btn>
                </template>
                <span>Contacts</span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    elevation="0"
                    fab
                    small
                    color="grey lighten-3"
                    v-on="on"
                    v-bind="attrs"
                    class="mx-1"
                    v-if="item.canHaveCodes"
                    @click="
                      $emit('showCodes', { entity: item, path: item.path })
                    "
                    :disabled="loading"
                  >
                    <v-icon>$code</v-icon>
                  </v-btn>
                </template>
                <span>Access Codes</span>
              </v-tooltip>
              <v-tooltip top v-if="item.children && item.children.length > 0">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    elevation="0"
                    fab
                    small
                    color="grey lighten-3"
                    @click="path = item.path"
                    v-on="on"
                    v-bind="attrs"
                    class="mx-1"
                    :disabled="loading"
                  >
                    <v-icon>$right</v-icon>
                  </v-btn>
                </template>
                <span>Select</span>
              </v-tooltip>
            </v-list-item-action>
          </v-list-item>
        </template>
      </v-list>
      <div class="grey lighten-3 py-2 px-4">
        <v-pagination
          total-visible="15"
          v-model="page"
          class="mx-4"
          :length="pageCount"
        ></v-pagination>
      </div>
    </v-card>
  </div>
</template>

<script>
import { settings as identitySettings } from "../../constants/Identity.js";
import sortBy from "../../utilities/sortBy.js";

export default {
  name: "EntitySelect",
  props: {
    value: String,
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    innerValue: null,
    path: null,
    search: "",
    page: 1,
    pageSize: 10,
  }),
  computed: {
    user() {
      return this.$store.state.user;
    },
    pathEntities() {
      var results = [];
      var pathParts =
        this.path?.split(identitySettings.entityPathSeparator) ?? [];

      let searchItems = this.items;

      pathParts.forEach((entityId, i) => {
        let target = searchItems.find((x) => x.id === entityId);
        if (target) {
          target.path = pathParts
            .slice(0, i + 1)
            .join(identitySettings.entityPathSeparator);
          results.push(target);
          searchItems = this.items.children;
        }
      });

      return results;
    },
    options() {
      var results = Array.from(
        this.pathEntities.length > 0
          ? this.pathEntities[this.pathEntities.length - 1].children
          : this.items
      ).map((entity) => {
        let x = { ...entity };
        x.displayAddress = "--";

        if (x.address1 || x.city) {
          x.displayAddress = `${x.address1} ${x.address2}, ${x.city}, ${x.state} ${x.zipCode}`;
        }

        if (!x.phone || x.phone.length === 0) x.displayPhone = "--";
        else {
          var phoneParts = x.phone.split("-");
          x.displayPhone = `(${phoneParts[0]}) ${phoneParts[1]}-${phoneParts[2]}`;
        }
        return x;
      });

      if (this.search?.length > 0) {
        let term = this.search.toLowerCase();
        results = results.filter((x) => {
          return [
            x.name,
            x.address1,
            x.address2,
            x.city,
            x.state,
            x.zipCode,
            x.phone,
          ].some((y) => y?.toLowerCase().indexOf(term) > -1);
        });
      }

      return sortBy(
        results.map((x) => {
          x.path = this.path
            ? this.path + identitySettings.entityPathSeparator + x.id
            : x.id;
          return x;
        }),
        "name"
      );
    },
    pageStart() {
      return this.pageSize * (this.page - 1);
    },
    pageEnd() {
      const maxIndex = this.options.length;
      let end = this.pageStart + this.pageSize;
      return end > maxIndex ? maxIndex : end;
    },
    pageCount() {
      return Math.ceil(this.options.length / this.pageSize);
    },
    pageItems() {
      return this.options.slice(this.pageStart, this.pageEnd).map((x) => {
        x.canHaveCodes = !x.children || x.children.length == 0;
        return x;
      });
    },
  },
  watch: {
    value(val, prev) {
      if (val != prev) this.innerValue = val + "";
    },
    innerValue(val, prev) {
      if (val != prev) this.$emit("input", val);
    },
    search(val, prev) {
      if (val != prev) this.page = 1;
    },
    path(val, prev) {
      if (val != prev) {
        this.search = "";
        this.page = 1;
      }
    },
  },
  methods: {
    titleCase(str) {
      str = str.toLowerCase().split(" ");
      for (var i = 0; i < str.length; i++) {
        str[i] = str[i].charAt(0).toUpperCase() + str[i].slice(1);
      }
      return str.join(" ");
    },
  },
  mounted() {
    if (this.value) this.innerValue = this.value + "";
  },
};
</script>
